// ProgressBar — in-content progress for a known operation (bulk import, a long
// job). Determinate (value-driven, fills L→R) or indeterminate (omit `value` →
// a constant-speed sliver sweep, for work of unknown length). On React Aria
// ([ADR-029]) for the progressbar role + aria value semantics; we own the track
// + fill markup + tokens.
//
// Distinct from the shell loaders: `Spinner` (in-control), `PageLoader` (app
// boot), `RouteProgress` (navigation — a decelerating trickle, not this constant
// sweep). The value label is formatted through `lib/format.ts` so it honours the
// user's numerals axis ([ADR-030]) — React Aria's own formatter does not.

import { ProgressBar as AriaProgressBar } from 'react-aria-components';
import { formatNumber } from '../../lib/format.js';

type Tone = 'brand' | 'success' | 'danger';

interface ProgressBarBaseProps {
  /** Current value. Omit for an indeterminate bar (unknown-length work). */
  value?: number;
  /** Range floor (default 0). */
  minValue?: number;
  /** Range ceiling (default 100). */
  maxValue?: number;
  /** Render the percentage text (right) — formatted via the numerals engine. */
  showValue?: boolean;
  /** Custom value text (e.g. "3 of 10"), overrides the default percentage. */
  valueLabel?: string;
  /** Fill tone (default `brand`). `success`/`danger` for completed / failed jobs. */
  tone?: Tone;
  className?: string;
}

// A progressbar must have an accessible name: a visible `label` or an `ariaLabel`.
export type ProgressBarProps = ProgressBarBaseProps &
  ({ label: string; ariaLabel?: never } | { label?: never; ariaLabel: string });

export function ProgressBar(props: ProgressBarProps) {
  const {
    value,
    minValue = 0,
    maxValue = 100,
    showValue = false,
    valueLabel,
    tone = 'brand',
    className = '',
  } = props;
  const label = 'label' in props ? props.label : undefined;
  const name = label ?? ('ariaLabel' in props ? props.ariaLabel : undefined);
  const isIndeterminate = value == null;

  // Percentage + formatted value text (numerals-aware) computed up front so it
  // feeds React Aria's aria-valuetext AND the visible text from one source.
  const pct = isIndeterminate
    ? 0
    : Math.round(((value - minValue) / (maxValue - minValue)) * 100);
  const valueText = isIndeterminate ? undefined : (valueLabel ?? `${formatNumber(pct)}%`);

  return (
    <AriaProgressBar
      value={value}
      minValue={minValue}
      maxValue={maxValue}
      isIndeterminate={isIndeterminate}
      valueLabel={valueText}
      aria-label={name}
      className={className}
    >
      {({ percentage }) => (
        <>
          {(label || (showValue && valueText)) && (
            // Decorative: the progressbar's name (aria-label) + aria-valuetext
            // already carry this to assistive tech.
            <div className="mb-1.5 flex items-baseline justify-between gap-2 text-sm" aria-hidden>
              {label && <span className="text-[var(--color-text-primary)]">{label}</span>}
              {showValue && valueText && (
                <span className="text-[var(--color-text-secondary)] tabular-nums">{valueText}</span>
              )}
            </div>
          )}
          <div className="progress-track" data-tone={tone}>
            {isIndeterminate ? (
              <div className="progress-indeterminate" />
            ) : (
              <div className="progress-fill" style={{ width: `${percentage}%` }} />
            )}
          </div>
        </>
      )}
    </AriaProgressBar>
  );
}
